import { useFormContext, useWatch } from "react-hook-form"
import { differenceInDays, isBefore, startOfDay } from "date-fns"
import { TimeWindowFormSchema } from "@/modules/patients/agenda/time-window/time-window-schema"

const MAX_ALLOWED_DAYS_FOR_TIME_WINDOW = 500

const removeTimeFromDate = (inputDate: Date): Date => {
  const newDate = new Date(inputDate)
  newDate.setHours(0, 0, 0, 0)
  return newDate
}

interface TimeWindowDateActions {
  handleSelectStartDate: (value?: Date) => void
  handleSelectEndDate: (value?: Date) => void
  updateDateRange: (startDate: Date, endDate: Date) => void
}

interface TimeWindowDateRangeStatus {
  isPast: boolean
  exceedsMaxDuration: boolean
  startDate: Date
}

// Pure setters — no useWatch, so callers don't re-render on date changes. Keep this out of the
// heavy form so it can drive the date range from the reason/referredBy handlers without subscribing.
export const useTimeWindowDateActions = (): TimeWindowDateActions => {
  const { setValue, trigger } = useFormContext<TimeWindowFormSchema>()

  // Setting one date only revalidates that field, so trigger the opposite end too — otherwise a stale
  // range error lingers after the "8 weeks" / "Until end of year" quick buttons (bug 40257).
  const handleSelectStartDate = (value?: Date) => {
    if (value) {
      setValue("dateFrom", removeTimeFromDate(value), { shouldValidate: true })
      trigger("dateTo")
    }
  }

  const handleSelectEndDate = (value?: Date) => {
    if (value) {
      setValue("dateTo", removeTimeFromDate(value), { shouldValidate: true })
      trigger("dateFrom")
    }
  }

  const updateDateRange = (startDate: Date, endDate: Date) => {
    setValue("dateFrom", removeTimeFromDate(startDate))
    setValue("dateTo", removeTimeFromDate(endDate))
    trigger(["dateFrom", "dateTo"])
  }

  return { handleSelectStartDate, handleSelectEndDate, updateDateRange }
}

// Subscribes to the watched dates — only render the component that actually shows the range status.
export const useTimeWindowDateRangeStatus = (): TimeWindowDateRangeStatus => {
  const { control } = useFormContext<TimeWindowFormSchema>()
  const watchedStartDate = useWatch({ control, name: "dateFrom" })
  const watchedEndDate = useWatch({ control, name: "dateTo" })

  // Derived straight from the watched dates — no state/effect needed.
  const isPast = isBefore(startOfDay(watchedEndDate), startOfDay(new Date()))
  const exceedsMaxDuration =
    differenceInDays(startOfDay(watchedEndDate), startOfDay(watchedStartDate)) > MAX_ALLOWED_DAYS_FOR_TIME_WINDOW

  return { isPast, exceedsMaxDuration, startDate: watchedStartDate }
}
